/**
 * Nachtrag-Version, Gastseite (docs/PLAN-NACHTRAG-VERSION.md, Einheit 4): Abschnitt "Änderung zu Ihrer Buchung".
 *
 * Wortgetreu nach dem freigegebenen Mockup (Antoine, 30.10.2026): Vergleichstabelle Bisher/Neu, Zahlung, Entscheidung.
 * Marken (neu/geändert/entfällt/unverändert) sind Rahmen und Schrift, keine Farbflächen. Alle Beträge kommen vom Server
 * (`lib/nachtrag-gast.ts`); der Browser formatiert nur. Antwort: `POST /api/public/offer/:token/nachtrag`
 * mit `{version, entscheidung, nachricht?, mengen?}`.
 *
 * Nur Deutsch (Sie-Form): der Nachtrag ist eine neue Funktion, die englische Fassung folgt, wenn ein Betrieb sie braucht.
 */
import { useEffect, useMemo, useState } from "react";
import type { PublicOffer } from "./types";
import { Button, Card, Icon, Input, Textarea } from "./components";
import { formatDate, money } from "./format";
import { nachtragAnsicht, nachtragFehlerText, type NachtragMarke, type NachtragZeile, type NachtragZeilenZustand } from "../lib/nachtrag-gast";
import { wahlgangStand } from "./angebot-auswahl-modell";

const MARKE_TEXT: Record<NachtragMarke, string> = { neu: "neu", geaendert: "geändert", entfallen: "entfällt", unveraendert: "unverändert" };
const MARKE_KLASSE: Record<NachtragMarke, string> = {
  neu: "border-tertiary text-tertiary dark:border-tertiary-fixed-dim dark:text-tertiary-fixed-dim",
  geaendert: "border-on-surface text-on-surface dark:border-inverse-on-surface dark:text-inverse-on-surface",
  entfallen: "border-error text-error",
  unveraendert: "border-outline-variant text-on-surface-variant dark:text-secondary-fixed-dim",
};

function Marke({ marke }: { marke: NachtragMarke }) {
  return (
    <span data-testid={`nachtrag-marke-${marke}`} className={`inline-block whitespace-nowrap rounded-md border px-1.5 text-[11px] font-bold leading-[18px] ${MARKE_KLASSE[marke]}`}>
      {MARKE_TEXT[marke]}
    </span>
  );
}

function Zelle({ stand, fett, leer = "–" }: { stand: NachtragZeilenZustand | null; fett?: boolean; leer?: string }) {
  if (!stand) return <span>{leer}</span>;
  return (
    <span className={fett ? "font-bold" : ""}>
      {stand.betragCents != null ? money(stand.betragCents) : (stand.detail ?? leer)}
      {stand.betragCents != null && stand.detail && <small className="block text-[12px] font-normal text-on-surface-variant dark:text-secondary-fixed-dim">{stand.detail}</small>}
    </span>
  );
}

const GRID = "grid grid-cols-[minmax(0,1.25fr)_minmax(0,.9fr)_minmax(0,.95fr)] gap-x-2 sm:grid-cols-[minmax(0,1.5fr)_minmax(0,1fr)_minmax(0,1fr)] sm:gap-x-3";
const TRENNER = "border-b border-outline-variant/40 dark:border-outline/30";

function Zeile({ z }: { z: NachtragZeile }) {
  return (
    <div className={`${GRID} ${TRENNER} items-start py-3 font-body-sm text-body-sm`} data-testid="nachtrag-zeile" data-marke={z.marke}>
      <div className="min-w-0 font-semibold text-on-surface dark:text-inverse-on-surface">
        {z.name}
        <span className="mt-0.5 block">
          <Marke marke={z.marke} />
        </span>
      </div>
      <div className="text-right tabular-nums text-on-surface-variant dark:text-secondary-fixed-dim">
        <Zelle stand={z.alt} />
      </div>
      <div className="text-right tabular-nums text-on-surface dark:text-inverse-on-surface">
        <Zelle stand={z.neu} fett={z.marke !== "unveraendert"} />
      </div>
    </div>
  );
}

function WerteZeile({ name, testId, marke, alt, neu }: { name: string; testId: string; marke: NachtragMarke; alt: string; neu: string }) {
  return (
    <div className={`${GRID} ${TRENNER} items-start py-3 font-body-sm text-body-sm`} data-testid={testId} data-marke={marke}>
      <div className="min-w-0 font-semibold text-on-surface dark:text-inverse-on-surface">
        {name}
        <span className="mt-0.5 block">
          <Marke marke={marke} />
        </span>
      </div>
      <div className="text-right tabular-nums text-on-surface-variant dark:text-secondary-fixed-dim">{alt}</div>
      <div className="text-right font-bold tabular-nums text-on-surface dark:text-inverse-on-surface">{neu}</div>
    </div>
  );
}

type Antwort = "zugestimmt" | "abgelehnt";

export function NachtragGast({
  offer,
  token,
  vorschau,
  onVeraltet,
}: {
  offer: PublicOffer;
  token: string | undefined;
  /** Betreiber-Vorschau: alles sichtbar, aber nichts sendbar (keine Gast-Willenserklärung). */
  vorschau: boolean;
  /** Der Nachtrag wurde inzwischen ersetzt (`version_veraltet`): die Seite lädt den aktuellen Stand neu. */
  onVeraltet?: () => void;
}) {
  const ansicht = useMemo(() => nachtragAnsicht(offer), [offer]);
  const [ablehnenOffen, setAblehnenOffen] = useState(false);
  const [nachricht, setNachricht] = useState("");
  // Vorbelegt mit der bisherigen Aufteilung (umgeschlüsselt auf die neuen Positionen): offen bleiben nur die zusätzlichen Gäste.
  const [mengen, setMengen] = useState<Record<string, Record<string, number>>>(() => ansicht?.vorbelegung ?? {});
  const [fehltZeigen, setFehltZeigen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [antwort, setAntwort] = useState<Antwort | null>(null);
  const [faellig, setFaellig] = useState<number | null>(null);
  // Mit Wahlgängen hängt die Zahlung von der Mengenauswahl ab: die Vorschau wird mit genau diesen Mengen neu gerechnet,
  // bevor „Zustimmen“ aktiv wird. Scheitert die Abfrage, zeigt die Seite nur den Hinweis auf den Zahlungslink per E-Mail.
  const [live, setLive] = useState<{ status: "leer" | "laedt" | "ok" | "fehler"; zahlung: NonNullable<NonNullable<typeof offer.nachtrag>["zahlung"]> | null }>({ status: "leer", zahlung: null });

  const stande = (ansicht?.wahlgaenge ?? []).map((w) => ({ w, stand: wahlgangStand(w, mengen, w.gaeste) }));
  const mengenKomplett = stande.every((s) => s.stand.ok);
  const braucheLive = !!ansicht && ansicht.wahlgaenge.length > 0;
  const mengenSchluessel = JSON.stringify(mengen);
  const nachtragVersion = ansicht?.version;
  useEffect(() => {
    if (!braucheLive || vorschau || !token || !mengenKomplett) {
      setLive({ status: "leer", zahlung: null });
      return;
    }
    let abgebrochen = false;
    setLive({ status: "laedt", zahlung: null });
    fetch(`/api/public/offer/${token}/nachtrag/vorschau`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify({ version: nachtragVersion, mengen }) })
      .then(async (res) => {
        const json = (await res.json().catch(() => ({}))) as { data?: { zahlung?: NonNullable<NonNullable<typeof offer.nachtrag>["zahlung"]> | null } };
        if (abgebrochen) return;
        setLive(res.ok && json.data?.zahlung ? { status: "ok", zahlung: json.data.zahlung } : { status: "fehler", zahlung: null });
      })
      .catch(() => {
        if (!abgebrochen) setLive({ status: "fehler", zahlung: null });
      });
    return () => {
      abgebrochen = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [braucheLive, vorschau, token, mengenKomplett, mengenSchluessel, nachtragVersion]);

  if (!ansicht) return null;
  const a = ansicht;

  function setzeMenge(gruppe: string, item: string, text: string) {
    const n = Math.max(0, Math.min(100000, Math.floor(Number(text.replace(/[^0-9]/g, "")) || 0)));
    setMengen((m) => ({ ...m, [gruppe]: { ...(m[gruppe] ?? {}), [item]: n } }));
    setFehltZeigen(false);
  }

  async function sende(entscheidung: "zustimmen" | "ablehnen") {
    if (vorschau || !token || busy) return;
    if (entscheidung === "zustimmen" && !mengenKomplett) {
      setFehltZeigen(true);
      setFehler(nachtragFehlerText("menuwahl_unvollstaendig"));
      return;
    }
    setBusy(true);
    setFehler(null);
    try {
      const body: Record<string, unknown> = { version: a.version, entscheidung };
      if (entscheidung === "ablehnen" && nachricht.trim()) body.nachricht = nachricht.trim();
      if (entscheidung === "zustimmen" && a.wahlgaenge.length > 0) body.mengen = mengen;
      const res = await fetch(`/api/public/offer/${token}/nachtrag`, { method: "POST", headers: { "content-type": "application/json" }, body: JSON.stringify(body) });
      const json = (await res.json().catch(() => ({}))) as { data?: { status?: string; faellig_cents?: number }; error?: string; message?: string };
      if (!res.ok) {
        setFehler(nachtragFehlerText(json.error, json.message));
        if (json.error === "version_veraltet") onVeraltet?.();
        return;
      }
      setFaellig(typeof json.data?.faellig_cents === "number" ? json.data.faellig_cents : (live.zahlung?.faelligCents ?? a.zahlung?.faelligCents ?? null));
      setAntwort(entscheidung === "zustimmen" ? "zugestimmt" : "abgelehnt");
    } catch {
      setFehler(nachtragFehlerText(undefined));
    } finally {
      setBusy(false);
    }
  }

  const z = braucheLive ? live.zahlung : a.zahlung;
  const zahlungOffen = braucheLive && !z;
  const zahlweg = z?.zahlweg ?? "stripe";
  const hinweisFaellig =
    zahlweg === "vor_ort" ? "Zahlung wie vereinbart vor Ort" : zahlweg === "rechnung" ? "Sie erhalten dafür eine Rechnung" : zahlweg === "kein_stripe" ? "Zahlung wie vereinbart" : "Sie erhalten dafür einen Zahlungslink per E-Mail";
  const diff = a.summe.differenzCents;
  return (
    <div className="mx-auto max-w-3xl space-y-5" data-testid="nachtrag-gast">
      <Card className="!p-5 sm:!p-6" aria-labelledby="nachtrag-h-aenderung" as="section">
        <h2 id="nachtrag-h-aenderung" className="font-headline-sm text-headline-sm font-bold text-on-surface dark:text-inverse-on-surface">
          Änderung zu Ihrer Buchung
        </h2>
        <p className="mb-4 mt-1 font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">Links sehen Sie Ihre bisherige Buchung, rechts die geänderte Fassung.</p>
        <div className={`${GRID} border-b border-outline-variant pb-2 text-[11px] font-bold uppercase tracking-[0.07em] text-on-surface-variant dark:text-secondary-fixed-dim`}>
          <span>Position</span>
          <span className="text-right">Bisher</span>
          <span className="text-right">Neu</span>
        </div>
        <WerteZeile name="Gäste" testId="nachtrag-gaeste" marke={a.gaeste.geaendert ? "geaendert" : "unveraendert"} alt={a.gaeste.alt != null ? String(a.gaeste.alt) : "–"} neu={a.gaeste.neu != null ? String(a.gaeste.neu) : "–"} />
        <WerteZeile
          name="Datum"
          testId="nachtrag-datum"
          marke={a.datum.geaendert ? "geaendert" : "unveraendert"}
          alt={a.datum.alt ? formatDate(a.datum.alt) : "–"}
          neu={a.datum.neu ? formatDate(a.datum.neu) : "–"}
        />
        {a.positionen.map((p) => (
          <Zeile key={p.key} z={p} />
        ))}
        <div className={`${GRID} items-start pt-3.5 font-bold text-on-surface dark:text-inverse-on-surface`} data-testid="nachtrag-summe">
          <div>Summe</div>
          <div className="text-right text-[17px] tabular-nums text-on-surface-variant dark:text-secondary-fixed-dim">{money(a.summe.altCents)}</div>
          <div className="text-right text-[17px] tabular-nums">{money(a.summe.neuCents)}</div>
        </div>
        <div className="mt-2.5 flex justify-between gap-3 border-t border-outline pt-2.5 font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface" data-testid="nachtrag-unterschied">
          <span className="min-w-0">Unterschied zur bisherigen Buchung</span>
          <b className="whitespace-nowrap tabular-nums">
            {diff > 0 ? "+ " : diff < 0 ? "− " : ""}
            {money(Math.abs(diff))}
          </b>
        </div>
      </Card>

      {a.wahlgaenge.length > 0 && !antwort && (
        <Card className="!p-5 sm:!p-6" aria-labelledby="nachtrag-h-wahl" as="section">
          <h2 id="nachtrag-h-wahl" className="font-headline-sm text-headline-sm font-bold text-on-surface dark:text-inverse-on-surface">
            Ihre Auswahl bei den Gängen
          </h2>
          <p className="mb-3 mt-1 font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">
            Die Gästezahl hat sich geändert. Bitte ordnen Sie jedem Gast ein Gericht zu.
          </p>
          <div className="space-y-5">
            {stande.map(({ w, stand }) => (
              <fieldset key={w.gruppeId} data-testid={`nachtrag-wahlgang-${w.gruppeId}`} className="min-w-0">
                <legend className="font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">
                  {w.gang ?? "Auswahl"} · {w.gaeste} Gäste
                </legend>
                <div className="mt-2 space-y-2">
                  {w.gerichte.map((g) => (
                    <div key={g.id} className="flex items-center justify-between gap-3">
                      <label htmlFor={`nachtrag-menge-${g.id}`} className="min-w-0 font-body-sm text-body-sm text-on-surface dark:text-inverse-on-surface">
                        {g.item.name}
                      </label>
                      <Input
                        id={`nachtrag-menge-${g.id}`}
                        className="!w-20 text-right"
                        inputMode="numeric"
                        aria-label={`Anzahl Gäste für ${g.item.name}`}
                        value={String(mengen[w.gruppeId]?.[g.id] ?? 0)}
                        onChange={(e) => setzeMenge(w.gruppeId, g.id, e.target.value)}
                        onFocus={(e) => e.target.select()}
                        disabled={vorschau}
                      />
                    </div>
                  ))}
                </div>
                <p
                  className={`mt-2 font-body-sm text-body-sm font-semibold ${stand.ok ? "text-tertiary dark:text-tertiary-fixed-dim" : fehltZeigen ? "text-error" : "text-on-surface-variant dark:text-secondary-fixed-dim"}`}
                  aria-live="polite"
                  data-testid={`nachtrag-wahlstand-${w.gruppeId}`}
                >
                  {stand.zuViel > 0 ? `${stand.zuViel} Gäste zu viel zugeordnet` : stand.ok ? `Alle ${w.gaeste} Gäste zugeordnet` : `Noch ${stand.offen} ${stand.offen === 1 ? "Gast" : "Gäste"} zuordnen`}
                </p>
              </fieldset>
            ))}
          </div>
        </Card>
      )}

      <Card className="!p-5 sm:!p-6" aria-labelledby="nachtrag-h-zahlung" as="section">
        <h2 id="nachtrag-h-zahlung" className="font-headline-sm text-headline-sm font-bold text-on-surface dark:text-inverse-on-surface">
          Zahlung
        </h2>
        <p className="mb-3 mt-1 font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">So wirkt sich die Änderung auf Ihre Zahlungen aus.</p>
        <dl data-testid="nachtrag-zahlung">
          {z ? (
            <>
              <ZahlRow titel="Bereits bezahlt" klein="Anzahlung für Ihre bisherige Buchung" wert={money(z.bereitsBezahltCents)} />
              <ZahlRow titel="Neue Gesamtsumme" wert={money(z.neuGesamtCents)} testId="nachtrag-neue-summe" />
              <div className="my-3 flex items-baseline justify-between gap-4 rounded-xl border border-primary bg-surface-container-low p-3.5 dark:bg-surface-variant/10" data-testid="nachtrag-faellig">
                <dt className="min-w-0 font-bold text-on-surface dark:text-inverse-on-surface">
                  Fällig nach Ihrer Zustimmung
                  <small className="block text-[12.5px] font-normal text-on-surface-variant dark:text-secondary-fixed-dim">{hinweisFaellig}</small>
                </dt>
                <dd className="m-0 whitespace-nowrap text-[20px] font-bold tabular-nums text-primary">{money(z.faelligCents)}</dd>
              </div>
              <ZahlRow titel="Rest wie vereinbart vor der Veranstaltung" wert={money(z.restCents)} />
            </>
          ) : zahlungOffen ? (
            <p className="m-0 font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim" data-testid="nachtrag-zahlung-hinweis">
              {live.status === "laedt" ? "Der fällige Betrag wird berechnet …" : live.status === "fehler" ? "Sie erhalten den Zahlungslink per E-Mail." : "Wird nach Ihrer Auswahl berechnet."}
            </p>
          ) : (
            // Einheit 5 (Geld): der Server füllt `nachtrag.zahlung` aus derselben Rechnung wie der spätere Ausgleich (GET /api/public/offer/:token).
            // Fehlt es (Rechnung nicht möglich, z. B. Kostenübernahme gilt), zeigt die Seite nur die Summe aus dem Snapshot.
            <ZahlRow titel="Neue Gesamtsumme" wert={money(a.summe.neuCents)} testId="nachtrag-neue-summe" />
          )}
        </dl>
      </Card>

      <Card className="!p-5 sm:!p-6" aria-labelledby="nachtrag-h-entscheidung" as="section">
        <h2 id="nachtrag-h-entscheidung" className="font-headline-sm text-headline-sm font-bold text-on-surface dark:text-inverse-on-surface">
          Ihre Entscheidung
        </h2>
        {!antwort && (
          <>
            <p className="mb-3.5 mt-1 text-[16px] font-bold text-on-surface dark:text-inverse-on-surface" data-testid="nachtrag-versprechen">
              Bis Sie zustimmen, gilt Ihre bisherige Buchung unverändert.
            </p>
            <div className="flex flex-col gap-2.5 sm:flex-row">
              <Button type="button" className="!min-h-12 !px-6 !py-3 sm:!min-w-[13rem]" disabled={vorschau || busy || (braucheLive && mengenKomplett && live.status === "laedt")} onClick={() => void sende("zustimmen")} data-testid="nachtrag-zustimmen">
                Änderung zustimmen
              </Button>
              <Button type="button" variant="secondary" className="!min-h-12 !px-6 !py-3" disabled={vorschau || busy} onClick={() => setAblehnenOffen(true)} data-testid="nachtrag-ablehnen">
                Ablehnen
              </Button>
            </div>
            {ablehnenOffen && (
              <div className="mt-4 border-t border-outline-variant/40 pt-4 dark:border-outline/30" data-testid="nachtrag-ablehnen-feld">
                <label htmlFor="nachtrag-nachricht" className="mb-1.5 block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface">
                  Nachricht an das Restaurant (optional)
                </label>
                <Textarea
                  id="nachtrag-nachricht"
                  rows={3}
                  maxLength={2000}
                  value={nachricht}
                  onChange={(e) => setNachricht(e.target.value)}
                  placeholder="z. B. Wir bleiben bei 65 Gästen, den Aperitivo hätten wir gern trotzdem."
                />
                <div className="mt-2.5 flex flex-wrap gap-2.5">
                  <Button type="button" disabled={vorschau || busy} onClick={() => void sende("ablehnen")} data-testid="nachtrag-ablehnung-senden">
                    Ablehnung senden
                  </Button>
                  <Button type="button" variant="secondary" disabled={busy} onClick={() => setAblehnenOffen(false)}>
                    Abbrechen
                  </Button>
                </div>
              </div>
            )}
            {vorschau && <p className="mt-3 font-body-sm text-body-sm text-on-surface-variant dark:text-secondary-fixed-dim">Vorschau: Antworten sind hier nicht möglich.</p>}
            {fehler && (
              <p role="alert" className="mt-3 flex items-start gap-2 font-body-sm text-body-sm font-semibold text-error" data-testid="nachtrag-fehler">
                <Icon name="error" className="mt-0.5 text-[18px]" />
                <span>{fehler}</span>
              </p>
            )}
          </>
        )}
        {antwort === "zugestimmt" && (
          <p role="status" className="mt-3 flex items-start gap-2.5 font-semibold text-tertiary dark:text-tertiary-fixed-dim" data-testid="nachtrag-bestaetigt">
            <Icon name="check_circle" className="mt-0.5 text-[20px]" />
            <span>
              Vielen Dank, die Änderung ist bestätigt.
              {faellig != null && faellig > 0 ? ` Den Zahlungslink über ${money(faellig)} senden wir Ihnen per E-Mail.` : ""}
            </span>
          </p>
        )}
        {antwort === "abgelehnt" && (
          <p role="status" className="mt-3 flex items-start gap-2.5 font-semibold text-tertiary dark:text-tertiary-fixed-dim" data-testid="nachtrag-abgelehnt">
            <Icon name="check_circle" className="mt-0.5 text-[20px]" />
            <span>Wir haben Ihre Antwort an das Restaurant weitergegeben. Ihre bisherige Buchung gilt unverändert.</span>
          </p>
        )}
      </Card>
    </div>
  );
}

function ZahlRow({ titel, klein, wert, testId }: { titel: string; klein?: string; wert: string; testId?: string }) {
  return (
    <div className={`flex items-baseline justify-between gap-4 py-2.5 ${TRENNER} last:border-b-0`}>
      <dt className="min-w-0 font-body-sm text-body-sm text-on-surface dark:text-inverse-on-surface">
        {titel}
        {klein && <small className="block text-[12.5px] text-on-surface-variant dark:text-secondary-fixed-dim">{klein}</small>}
      </dt>
      <dd className="m-0 whitespace-nowrap font-semibold tabular-nums text-on-surface dark:text-inverse-on-surface" data-testid={testId}>
        {wert}
      </dd>
    </div>
  );
}
